import React, { useState } from "react";
import { FileText, ChevronDown, ChevronUp } from "lucide-react";

const WppIcon = () => (
  <svg viewBox="0 0 448 512" className="w-4 h-4 text-green-500" fill="currentColor">
    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
  </svg>
);

function buildWppUrl(phone, cnj, nome, cpf) {
  if (!phone) return null;
  const msg = `Olá, tenho dúvidas sobre o processo ${cnj || ""}. Nome: ${nome || ""} CPF: ${cpf || ""}`;
  return `https://wa.me/55${phone}?text=${encodeURIComponent(msg)}`;
}

function CondicoesBlock({ processo, userData, forceExpand = false }) {
  const [expanded, setExpanded] = useState(false);
  const condicoes = processo?.condicoes_cessao;
  const pontos = Array.isArray(condicoes?.pontos) ? condicoes.pontos : [];
  const clausulas = Array.isArray(condicoes?.clausulas) ? condicoes.clausulas : [];
  const wppUrl = buildWppUrl(
    processo?.whatsapp_confirmacao,
    processo?.cnj,
    userData?.nomeCompleto,
    userData?.cpf,
  );

  if (!pontos.length && !clausulas.length) return null;

  const isExpanded = forceExpand || expanded;

  const content = (
    <>
      {pontos.length > 0 && (
        <div className="bg-statusInfoBg border border-statusInfo/20 rounded-xl p-5 space-y-3">
          {pontos.map((ponto, idx) => (
            <div key={idx} className="flex gap-2.5 text-sm text-statusInfo/80 leading-snug">
              <span className="text-blue-600 font-black shrink-0 mt-px">•</span>
              <span dangerouslySetInnerHTML={{ __html: ponto }} />
            </div>
          ))}
        </div>
      )}

      {clausulas.length > 0 && (
        <div className="bg-muted border border-border rounded-xl p-5 space-y-4 text-[13px] text-foreground leading-relaxed">
          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground pb-2 border-b border-border">
            Cláusulas Contratuais Relevantes
          </p>
          {clausulas.map((c, idx) => (
            <div key={idx}>
              <span className="font-bold text-foreground">{c.titulo}: </span>
              <span dangerouslySetInnerHTML={{ __html: c.texto }} />
            </div>
          ))}
        </div>
      )}

      {wppUrl && (
        <div className="text-right">
          <a
            href={wppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-statusInfo underline underline-offset-2"
          >
            <WppIcon />
            Dúvidas?
          </a>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-3">
      <div className={`space-y-3 overflow-hidden transition-all duration-300 ${isExpanded ? "" : "md:max-h-none max-h-48 relative"}`}>
        {content}
        {!isExpanded && (
          <div className="md:hidden absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        )}
      </div>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="md:hidden flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-statusInfo transition-colors"
      >
        {isExpanded
          ? <><ChevronUp className="w-3.5 h-3.5" /> Recolher</>
          : <><ChevronDown className="w-3.5 h-3.5" /> Ler condições completas</>}
      </button>
    </div>
  );
}

export function CondicoesCessaoSection({ processos, userData }) {
  const comCondicoes = (processos ?? []).filter(p => {
    const c = p.processo?.condicoes_cessao;
    return p.processo?.tem_cessao !== false && c &&
      ((Array.isArray(c?.pontos) && c.pontos.length > 0) ||
       (Array.isArray(c?.clausulas) && c.clausulas.length > 0));
  });

  if (!comCondicoes.length) return null;

  const multiple = comCondicoes.length > 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-md bg-gradient-to-br from-blue-600 to-blue-500 shrink-0">
          <FileText className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
          Condições da Cessão de Crédito
        </h2>
      </div>

      <div className="space-y-6">
        {comCondicoes.map((p, idx) => (
          <div key={p.processo_id ?? idx}>
            {multiple && (
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                {p.processo?.nome_processo || p.processo?.cnj}
              </p>
            )}
            <CondicoesBlock processo={p.processo} userData={userData} />
          </div>
        ))}
      </div>
    </div>
  );
}
